import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Typography,
} from "@mui/material";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {AuthContext} from "../../../context/AuthContext";
import {FetchContext} from "../../../context/FetchContext";

export type ZoomRecording = {
    id: string;
    fileType: string;
    fileExtension: string;
    fileSize?: number;
    status: string;
    recordingType?: string;
    recordingStart?: string;
    recordingEnd?: string;
    isPrimary?: boolean;
};

export type ZoomMeeting = {
    key: string;
    uuid?: string;
    id?: string;
    topic: string;
    startTime?: string;
    duration?: number;
    recordings: ZoomRecording[];
};

type ZoomImportDialogProps = {
    open: boolean;
    onClose: () => void;
    projectId: string | null | undefined;
    onImportStarted?: (count: number) => void;
};

const mediaTypeFor = (recording: ZoomRecording): string =>
    String(recording.fileExtension || recording.fileType || "").trim().toUpperCase();

export const isImportableZoomRecording = (recording: ZoomRecording): boolean => {
    const mediaType = mediaTypeFor(recording);
    return String(recording.status || "").trim().toLowerCase() === "completed" &&
        (mediaType === "MP4" || mediaType === "M4A");
};

const normalizeRecording = (value: any): ZoomRecording | null => {
    const id = String(value?.id ?? value?.recordingId ?? value?.recordingFileId ?? "").trim();
    if (!id) return null;
    return {
        id,
        fileType: String(value?.fileType ?? value?.file_type ?? "").trim(),
        fileExtension: String(value?.fileExtension ?? value?.file_extension ?? "").trim(),
        fileSize: Number.isFinite(Number(value?.fileSize ?? value?.file_size))
            ? Number(value?.fileSize ?? value?.file_size)
            : undefined,
        status: String(value?.status ?? "").trim(),
        recordingType: String(value?.recordingType ?? value?.recording_type ?? "").trim() || undefined,
        recordingStart: String(value?.recordingStart ?? value?.recording_start ?? "").trim() || undefined,
        recordingEnd: String(value?.recordingEnd ?? value?.recording_end ?? "").trim() || undefined,
        isPrimary: value?.isPrimary === true || value?.primary === true,
    };
};

export const normalizeZoomMeetings = (payload: any): ZoomMeeting[] => {
    const rawMeetings = Array.isArray(payload?.meetings) ? payload.meetings : [];
    return rawMeetings.flatMap((value: any, index: number) => {
        const rawRecordings = Array.isArray(value?.recordings)
            ? value.recordings
            : (Array.isArray(value?.files) ? value.files : []);
        const recordings = rawRecordings
            .map(normalizeRecording)
            .filter((recording: ZoomRecording | null): recording is ZoomRecording =>
                Boolean(recording && isImportableZoomRecording(recording))
            );
        if (recordings.length === 0) return [];

        const uuid = String(value?.uuid ?? "").trim() || undefined;
        const id = String(value?.id ?? value?.meetingId ?? "").trim() || undefined;
        return [{
            key: uuid || id || `meeting-${index}`,
            uuid,
            id,
            topic: String(value?.topic ?? value?.title ?? "Untitled Zoom meeting").trim() || "Untitled Zoom meeting",
            startTime: String(value?.startTime ?? value?.start_time ?? "").trim() || undefined,
            duration: Number.isFinite(Number(value?.duration)) ? Number(value.duration) : undefined,
            recordings,
        }];
    });
};

export const defaultZoomRecordingIds = (meetings: ZoomMeeting[]): Set<string> => {
    const selected = new Set<string>();
    for (const meeting of meetings) {
        const mp4s = meeting.recordings.filter((recording) => mediaTypeFor(recording) === "MP4");
        const primary = mp4s.find((recording) => recording.isPrimary) ?? mp4s[0];
        if (primary) selected.add(primary.id);
    }
    return selected;
};

const formatBytes = (bytes?: number): string => {
    if (!Number.isFinite(bytes) || !bytes || bytes <= 0) return "";
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const formatMeetingDate = (value?: string): string => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

export const formatZoomMeetingMeta = (startTime?: string, durationMinutes?: number): string => {
    const date = formatMeetingDate(startTime);
    const duration = Number.isFinite(durationMinutes) && Number(durationMinutes) > 0
        ? `${Math.round(Number(durationMinutes))} min`
        : "";
    return [date, duration].filter(Boolean).join(" · ");
};

const recordingViewLabel = (recording: ZoomRecording): string => {
    const view = String(recording.recordingType || "").replace(/_/g, " ").trim().toLowerCase();
    if (view) return `${view.charAt(0).toUpperCase()}${view.slice(1)}`;
    return mediaTypeFor(recording) === "M4A" ? "Audio only" : "Video recording";
};

const errorMessageFor = (error: any, fallback: string): string => {
    const responseMessage = error?.response?.data?.message || error?.response?.data?.error;
    if (typeof responseMessage === "string" && responseMessage.trim()) return responseMessage.trim();
    return fallback;
};

const ZoomImportDialog: React.FC<ZoomImportDialogProps> = ({
    open,
    onClose,
    projectId,
    onImportStarted,
}) => {
    const authContext = useContext(AuthContext);
    const fetchContext = useContext(FetchContext);
    const requestSequenceRef = useRef(0);
    const [loading, setLoading] = useState(false);
    const [connected, setConnected] = useState<boolean | null>(null);
    const [meetings, setMeetings] = useState<ZoomMeeting[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [connecting, setConnecting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const authConfig = useCallback(() => ({
        headers: {Authorization: `Bearer ${authContext.getToken()}`},
    }), [authContext]);

    const loadDialog = useCallback(async () => {
        const requestSequence = ++requestSequenceRef.current;
        setLoading(true);
        setError(null);
        setConnected(null);
        setMeetings([]);
        setSelectedIds(new Set());

        try {
            const connectionResponse = await fetchContext.authAxios.get(
                "/integrations/zoom/connection",
                authConfig(),
            );
            if (requestSequence !== requestSequenceRef.current) return;
            const isConnected = connectionResponse?.data?.connected === true;
            setConnected(isConnected);
            if (!isConnected) return;

            const recordingsResponse = await fetchContext.authAxios.get(
                "/integrations/zoom/recordings",
                authConfig(),
            );
            if (requestSequence !== requestSequenceRef.current) return;
            const nextMeetings = normalizeZoomMeetings(recordingsResponse?.data);
            setMeetings(nextMeetings);
            setSelectedIds(defaultZoomRecordingIds(nextMeetings));
        } catch (requestError) {
            if (requestSequence !== requestSequenceRef.current) return;
            setError(errorMessageFor(requestError, "Unable to load your Zoom recordings."));
        } finally {
            if (requestSequence === requestSequenceRef.current) setLoading(false);
        }
    }, [authConfig, fetchContext]);

    useEffect(() => {
        if (!open) {
            requestSequenceRef.current += 1;
            return;
        }
        void loadDialog();
    }, [loadDialog, open]);

    const selectedCount = selectedIds.size;
    const handleConnect = async () => {
        const pid = String(projectId || "").trim();
        if (!pid) {
            setError("This project is not available.");
            return;
        }
        setConnecting(true);
        setError(null);
        try {
            const response = await fetchContext.authAxios.post(
                "/integrations/zoom/authorize",
                {projectId: pid},
                {...authConfig(), withCredentials: true},
            );
            const authorizationUrl = String(response?.data?.authorizationUrl || "").trim();
            if (!authorizationUrl) throw new Error("Missing Zoom authorization URL");
            window.location.assign(authorizationUrl);
        } catch (requestError) {
            setError(errorMessageFor(requestError, "Unable to connect to Zoom."));
            setConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        setDisconnecting(true);
        setError(null);
        try {
            await fetchContext.authAxios.delete("/integrations/zoom/connection", authConfig());
            setConnected(false);
            setMeetings([]);
            setSelectedIds(new Set());
        } catch (requestError) {
            setError(errorMessageFor(requestError, "Unable to disconnect Zoom."));
        } finally {
            setDisconnecting(false);
        }
    };

    const handleImport = async () => {
        const pid = String(projectId || "").trim();
        if (!pid || selectedCount === 0) return;
        setImporting(true);
        setError(null);
        try {
            await fetchContext.authAxios.post(
                "/integrations/zoom/imports",
                {projectId: pid, recordingIds: Array.from(selectedIds)},
                authConfig(),
            );
            onClose();
            onImportStarted?.(selectedCount);
        } catch (requestError) {
            setError(errorMessageFor(requestError, "Unable to start the Zoom import."));
        } finally {
            setImporting(false);
        }
    };

    const toggleRecording = (recordingId: string) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(recordingId)) next.delete(recordingId);
            else next.add(recordingId);
            return next;
        });
    };

    const busy = connecting || importing || disconnecting;

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="zoom-import-title"
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    border: "1px solid var(--app-border-subtle)",
                    backgroundColor: "var(--app-bg-surface)",
                    backgroundImage: "none",
                    boxShadow: "0 24px 72px rgba(15, 23, 42, 0.18)",
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle
                sx={{
                    px: 3,
                    py: 2.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    borderBottom: "1px solid var(--app-border-subtle)",
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        flexShrink: 0,
                        borderRadius: "12px",
                        display: "grid",
                        placeItems: "center",
                        color: "#2D8CFF",
                        backgroundColor: "rgba(45, 140, 255, 0.12)",
                    }}
                >
                    <CloudDownloadOutlinedIcon sx={{fontSize: 24}} />
                </Box>
                <Box sx={{minWidth: 0, flex: 1}}>
                    <Typography id="zoom-import-title" sx={{fontSize: 20, lineHeight: 1.25, fontWeight: 700, color: "var(--app-text-primary)"}}>
                        Import from Zoom
                    </Typography>
                    <Typography sx={{fontSize: 13, lineHeight: 1.4, color: "var(--app-text-muted)", mt: 0.35}}>
                        Choose files from your Zoom cloud recordings.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close Zoom import"
                    onClick={onClose}
                    disabled={busy}
                    size="small"
                    sx={{
                        alignSelf: "flex-start",
                        color: "var(--app-text-muted)",
                        "&:hover": {backgroundColor: "var(--app-bg-subtle)"},
                    }}
                >
                    <CloseRoundedIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{p: "24px !important"}}>
                {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

                {loading ? (
                    <Box sx={{minHeight: 210, display: "flex", alignItems: "center", justifyContent: "center", gap: 1.25}}>
                        <CircularProgress size={20} thickness={5} />
                        <Typography sx={{fontSize: 13, color: "var(--app-text-muted)"}}>Loading Zoom…</Typography>
                    </Box>
                ) : connected === false ? (
                    <Box
                        sx={{
                            minHeight: 210,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            px: {xs: 2.5, sm: 4},
                            py: 3,
                            border: "1px solid var(--app-border-subtle)",
                            borderRadius: "14px",
                            backgroundColor: "var(--app-bg-subtle)",
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                mb: 1.5,
                                borderRadius: "50%",
                                display: "grid",
                                placeItems: "center",
                                color: "#2D8CFF",
                                border: "1px solid rgba(45, 140, 255, 0.22)",
                                backgroundColor: "var(--app-bg-surface)",
                            }}
                        >
                            <CloudDownloadOutlinedIcon sx={{fontSize: 26}} />
                        </Box>
                        <Typography sx={{fontSize: 17, lineHeight: 1.3, fontWeight: 700, color: "var(--app-text-primary)"}}>
                            Connect your Zoom account
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleConnect}
                            disabled={connecting || !projectId}
                            startIcon={connecting ? <CircularProgress size={15} color="inherit" /> : undefined}
                            sx={{
                                minHeight: 40,
                                mt: 1.75,
                                px: 2.5,
                                textTransform: "none",
                                borderRadius: "10px",
                                fontWeight: 600,
                                boxShadow: "none",
                            }}
                        >
                            {connecting ? "Opening Zoom…" : "Connect Zoom"}
                        </Button>
                    </Box>
                ) : connected === true ? (
                    <Box>
                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5}}>
                            <Typography sx={{fontSize: 13.5, fontWeight: 650, color: "var(--app-text-primary)"}}>
                                Recordings
                            </Typography>
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.65,
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: "999px",
                                        border: "1px solid var(--app-border-subtle)",
                                        backgroundColor: "var(--app-bg-subtle)",
                                    }}
                                >
                                    <Box sx={{width: 6, height: 6, borderRadius: "50%", backgroundColor: "#16A34A"}} />
                                    <Typography sx={{fontSize: 11.5, fontWeight: 600, color: "var(--app-text-muted)"}}>
                                        Connected
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    color="inherit"
                                    onClick={handleDisconnect}
                                    disabled={disconnecting || importing}
                                    sx={{minWidth: 0, px: 0.75, textTransform: "none", fontSize: 12, color: "var(--app-text-muted)"}}
                                >
                                    {disconnecting ? "Disconnecting…" : "Disconnect"}
                                </Button>
                            </Box>
                        </Box>

                        {error ? null : meetings.length === 0 ? (
                            <Box sx={{minHeight: 190, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", px: 4, borderRadius: "12px", border: "1px solid var(--app-border-subtle)", backgroundColor: "var(--app-bg-subtle)"}}>
                                <Typography sx={{fontSize: 13, lineHeight: 1.5, color: "var(--app-text-muted)"}}>
                                    No completed Zoom recordings found.
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{display: "flex", flexDirection: "column", gap: 1.25, maxHeight: "52vh", overflowY: "auto", pr: 0.5}}>
                                {meetings.map((meeting) => (
                                    <Box
                                        key={meeting.key}
                                        sx={{
                                            border: "1px solid var(--app-border-subtle)",
                                            borderRadius: "12px",
                                            overflow: "hidden",
                                            backgroundColor: "var(--app-bg-surface)",
                                        }}
                                    >
                                        <Box sx={{px: 1.75, py: 1.25, backgroundColor: "var(--app-bg-subtle)"}}>
                                            <Typography sx={{fontSize: 13.5, fontWeight: 650, color: "var(--app-text-primary)"}} noWrap>
                                                {meeting.topic}
                                            </Typography>
                                            {(meeting.startTime || meeting.duration) && (
                                                <Typography sx={{mt: 0.2, fontSize: 11.5, color: "var(--app-text-muted)"}}>
                                                    {formatZoomMeetingMeta(meeting.startTime, meeting.duration)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{display: "flex", flexDirection: "column"}}>
                                            {meeting.recordings.map((recording) => {
                                                const size = formatBytes(recording.fileSize);
                                                const type = mediaTypeFor(recording);
                                                return (
                                                    <FormControlLabel
                                                        key={recording.id}
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={selectedIds.has(recording.id)}
                                                                onChange={() => toggleRecording(recording.id)}
                                                                sx={{color: "var(--app-text-subtle)", "&.Mui-checked": {color: "#2D8CFF"}}}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{display: "flex", alignItems: "center", gap: 0.9, minWidth: 0, width: "100%"}}>
                                                                <Box
                                                                    sx={{
                                                                        flexShrink: 0,
                                                                        px: 0.75,
                                                                        py: 0.3,
                                                                        borderRadius: "6px",
                                                                        fontSize: 10.5,
                                                                        lineHeight: 1.25,
                                                                        fontWeight: 700,
                                                                        color: "#2D8CFF",
                                                                        backgroundColor: "rgba(45, 140, 255, 0.11)",
                                                                    }}
                                                                >
                                                                    {type}
                                                                </Box>
                                                                <Typography noWrap sx={{minWidth: 0, fontSize: 12.5, color: "var(--app-text-primary)"}}>
                                                                    {recordingViewLabel(recording)}
                                                                </Typography>
                                                                {size && (
                                                                    <Typography sx={{ml: "auto", flexShrink: 0, fontSize: 11.5, color: "var(--app-text-muted)"}}>
                                                                        {size}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                        sx={{
                                                            m: 0,
                                                            px: 0.75,
                                                            minHeight: 44,
                                                            borderTop: "1px solid var(--app-border-subtle)",
                                                            backgroundColor: selectedIds.has(recording.id)
                                                                ? "rgba(45, 140, 255, 0.06)"
                                                                : "transparent",
                                                            "& .MuiFormControlLabel-label": {flex: 1, minWidth: 0},
                                                            "&:hover": {backgroundColor: "var(--app-bg-subtle)"},
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                ) : null}
            </DialogContent>

            {connected === true && meetings.length > 0 && (
                <DialogActions sx={{px: 3, py: 2, gap: 1, borderTop: "1px solid var(--app-border-subtle)"}}>
                    <Typography sx={{mr: "auto", fontSize: 12.5, color: "var(--app-text-muted)"}}>
                        {selectedCount === 0 ? "None selected" : `${selectedCount} selected`}
                    </Typography>
                    <Button onClick={onClose} disabled={busy} sx={{textTransform: "none", color: "var(--app-text-muted)"}}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleImport}
                        disabled={importing || disconnecting || selectedCount === 0}
                        startIcon={importing ? <CircularProgress size={15} color="inherit" /> : undefined}
                        sx={{textTransform: "none", borderRadius: "9px", boxShadow: "none", fontWeight: 600}}
                    >
                        {importing ? "Starting import…" : "Import selected"}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default ZoomImportDialog;
